import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ClientHeader from "../components/ClientHeader";
import NavSidebar from "../components/NavSidebar";
import InfoTip from "../components/shared/InfoTip";
import { useNotification } from "../contexts/NotificationContext";
import { useFeatures } from "../contexts/FeaturesContext";
import { useTheme } from "../contexts/ThemeContext";
import { useView } from "../contexts/ViewContext";
import {
  getProjectsByClient,
  getUserDetails,
  getMetricsData,
  getGrowthComparison,
} from "../api";
import SubscribeUpsellModal from "../components/SubscribeUpsellModal";
import DashboardTileGrid from "../components/dashboard/DashboardTileGrid";

import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend,
} from "recharts";

// jspdf / jspdf-autotable / html2canvas are loaded on demand in downloadPDF
// (kept out of the initial bundle — only needed when exporting a PDF).
import Papa from "papaparse";

/* ----------------------------- helpers ----------------------------- */
const fmtDate = (d) => {
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(dt?.getTime?.())) return "";
    return dt.toISOString().slice(0, 10);
  } catch { return ""; }
};
const startOfMonth = (d = new Date()) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth   = (d = new Date()) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

function ClientDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showInfo } = useNotification();
  const { hasFeature } = useFeatures();
  const { isDarkMode } = useTheme();
  const view = useView();

  const stateClientId  = location.state?.clientId;
  const storedClientId = localStorage.getItem("client_id");
  const clientId = stateClientId ?? (storedClientId ? Number(storedClientId) : null);

  const [subs, setSubs] = useState({ is_chat_subscribed: false, is_voice_subscribed: false });
  const [loadingSubs, setLoadingSubs] = useState(true);
  const [subsError, setSubsError] = useState("");

  const [product, setProduct] = useState(null);

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState("");

  const [query, setQuery] = useState("");

  // Guard against literal "undefined" from stale prior writes (see AuthContext.safeStore).
  const _rawUsername = localStorage.getItem("username");
  const username = (_rawUsername && _rawUsername !== "undefined") ? _rawUsername : "client_user";

  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscribeProduct, setSubscribeProduct] = useState("chat");

  // Use ViewContext project + dates (synced with ClientHeader dropdown)
  const selectedProjectId = view.selectedProjectId;
  const setSelectedProjectId = (id) => view.selectProject(id);
  const rangeStart = view.rangeStart;
  const rangeEnd = view.rangeEnd;

  const [metrics, setMetrics] = useState({
    timeSeriesData: [],
    summary: { conversations: 0, messages: 0, bookings: 0 },
  });
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState("");

  const [growth, setGrowth] = useState({
    currentPeriod: { conversations: 0, messages: 0, bookings: 0 },
    previousPeriod:{ conversations: 0, messages: 0, bookings: 0 },
    growth:        { conversations: 0, messages: 0, bookings: 0 },
  });
  const [growthLoading, setGrowthLoading] = useState(false);
  const [growthError, setGrowthError] = useState("");

  const chartsRef = useRef(null);

  const openSubscribe = (which) => {
    if (!clientId) return;
    setSubscribeProduct(which || "chat");
    setSubscribeOpen(true);
  };

  const handleStartSubscription = async ({ product: which }) => {
    showInfo(
      'Request Received', 
      'Thank you for your interest. A member of our team will contact you shortly.',
      0 // Don't auto-dismiss - requires manual close
    );
    setSubscribeOpen(false);
    try {
      setLoadingSubs(true);
      const resp = await getUserDetails(clientId);
      const data = resp?.data ?? resp ?? {};
      const next = {
        is_chat_subscribed: !!data.is_chat_subscribed,
        is_voice_subscribed: !!data.is_voice_subscribed,
      };
      setSubs(next);
      if (which === "chat" && next.is_chat_subscribed) setProduct("chat");
      if (which === "voice" && next.is_voice_subscribed) setProduct("voice");
    } finally { setLoadingSubs(false); }
  };

  /* ---------------- Load subscription + pick default product ---------------- */
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        if (!clientId) throw new Error("Missing client id. Please sign in again.");
        setLoadingSubs(true); setSubsError("");
        const resp = await getUserDetails(clientId);
        const data = resp?.data ?? resp ?? {};
        const next = {
          is_chat_subscribed: !!data.is_chat_subscribed,
          is_voice_subscribed: !!data.is_voice_subscribed,
        };
        if (!on) return;
        setSubs(next);
        if (!next.is_chat_subscribed && !next.is_voice_subscribed) { openSubscribe("chat"); return; }
        if (next.is_chat_subscribed) setProduct("chat");
        else if (next.is_voice_subscribed) setProduct("voice");
      } catch (e) {
        if (!on) return;
        setSubsError(e?.response?.data?.message || e?.message || "Failed to load subscription info.");
        setSubs({ is_chat_subscribed: false, is_voice_subscribed: false });
        setProduct(null);
      } finally { if (on) setLoadingSubs(false); }
    })();
    return () => { on = false; };
  }, [clientId]);

  /* -------- Load projects when product changes (single effect, normalized) --- */
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        if (!clientId || !product) {
          setProjects([]);
          setSelectedProjectId(null);
          return;
        }
        setLoadingProjects(true);
        setProjectsError("");

        const resp = await getProjectsByClient(clientId, 1, 200, product);
        const root = resp?.data ?? resp ?? {};
        const rows = root?.data ?? root?.rows ?? root?.projects ?? root?.data?.data ?? [];
        if (!on) return;

        const list = Array.isArray(rows) ? rows : [];

        const filtered = list.filter((p) => {
          const t = (p.type || p.project_type || "").toString().toLowerCase();
          return t ? t.includes(product) : true;
        });

        const mapped = filtered.map((p) => ({
          id: String(p.id ?? p.project_id ?? p._id ?? ""),
          name: p.name ?? p.project_name ?? `Project ${p.id ?? ""}`,
          logo: p.logoUrl ?? p.logo ?? null,
        }));

        const sortedAZ = mapped.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        const firstAlphaId = sortedAZ[0]?.id ?? null;

        setProjects(mapped);
        setSelectedProjectId(firstAlphaId); 
      } catch (e) {
        if (!on) return;
        setProjectsError(e?.response?.data?.message || e?.message || "Failed to load projects.");
        setProjects([]);
        setSelectedProjectId(null);
      } finally {
        if (on) setLoadingProjects(false);
      }
    })();
    return () => { on = false; };
  }, [clientId, product]);

  const visibleProjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(p => (p.name || "").toLowerCase().includes(q));
  }, [projects, query]);

  const [viewMode, setViewMode] = useState(localStorage.getItem("proj_view") || "grid");
  useEffect(() => localStorage.setItem("proj_view", viewMode), [viewMode]);

  const [sortOrder, setSortOrder] = useState(localStorage.getItem("proj_sort") || "az"); 
  useEffect(() => localStorage.setItem("proj_sort", sortOrder), [sortOrder]);

  const sortedProjects = useMemo(() => {
    const arr = [...visibleProjects];
    const dir = sortOrder === "az" ? 1 : -1;
    arr.sort((a,b) => dir * (a.name || "").localeCompare(b.name || ""));
    return arr;
  }, [visibleProjects, sortOrder]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(3);
  useEffect(() => setPage(1), [query, product, projects.length, sortOrder]);
  const totalPages = Math.max(1, Math.ceil(sortedProjects.length / pageSize));
  const pageProjects = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedProjects.slice(start, start + pageSize);
  }, [sortedProjects, page, pageSize]);
  const goPage = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  const projectIdForApi = useMemo(() => {
    if (!selectedProjectId) return null;
    const n = Number(selectedProjectId);
    return Number.isNaN(n) || n === 0 ? selectedProjectId : n;
  }, [selectedProjectId]);

  const loadMetrics = useCallback(async () => {
    if (!projectIdForApi) return;
    setMetricsLoading(true); setMetricsError("");
    try {
      const resp = await getMetricsData(projectIdForApi, { start_date: rangeStart, end_date: rangeEnd });
      const data = resp?.data ?? resp ?? {};
      setMetrics({
        timeSeriesData: Array.isArray(data.timeSeriesData) ? data.timeSeriesData : [],
        summary: data.summary || { conversations: 0, messages: 0, bookings: 0 },
      });
    } catch (e) {
      setMetricsError(e?.message || "Failed to fetch metrics data");
      setMetrics({ timeSeriesData: [], summary: { conversations: 0, messages: 0, bookings: 0 } });
    } finally { setMetricsLoading(false); }
  }, [projectIdForApi, rangeStart, rangeEnd]);

  const loadGrowth = useCallback(async () => {
    if (!projectIdForApi) return;
    const curStart = new Date(rangeStart);
    const curEnd   = new Date(rangeEnd);
    const days     = Math.max(1, Math.ceil((curEnd - curStart) / (1000*60*60*24)) + 1);
    const prevEnd  = new Date(curStart); prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart= new Date(prevEnd);  prevStart.setDate(prevStart.getDate() - (days - 1));
    setGrowthLoading(true); setGrowthError("");
    try {
      const resp = await getGrowthComparison(projectIdForApi, {
        current_start: fmtDate(curStart), current_end: fmtDate(curEnd),
        previous_start: fmtDate(prevStart), previous_end: fmtDate(prevEnd),
      });
      const data = resp?.data ?? resp ?? {};
      setGrowth({
        currentPeriod: data.currentPeriod || { conversations: 0, messages: 0, bookings: 0 },
        previousPeriod:data.previousPeriod|| { conversations: 0, messages: 0, bookings: 0 },
        growth:        data.growth        || { conversations: 0, messages: 0, bookings: 0 },
      });
    } catch (e) {
      setGrowthError(e?.message || "Failed to fetch growth comparison");
      setGrowth({
        currentPeriod: { conversations: 0, messages: 0, bookings: 0 },
        previousPeriod:{ conversations: 0, messages: 0, bookings: 0 },
        growth:        { conversations: 0, messages: 0, bookings: 0 },
      });
    } finally { setGrowthLoading(false); }
  }, [projectIdForApi, rangeStart, rangeEnd]);

  // 2026-07-27 — /client-dashboard now renders <DashboardTileGrid /> (see the
  // active return at line ~472). The legacy metrics+growth JSX below is
  // unreachable, so firing loadMetrics/loadGrowth into it just produced
  // wasted RPCs — and worse, one of the calls used a wrong signature
  // (object literal where positional args were expected), which surfaced as
  // 404s for fn_get_metrics_data / fn_get_growth_comparison on every
  // /client-dashboard load. Removing the effect stops the noise. The
  // loadMetrics/loadGrowth callbacks are kept for now so the unreachable JSX
  // below still parses — final delete once the tile grid is stable.
  // eslint-disable-next-line no-unused-expressions
  void loadMetrics; void loadGrowth;

  const chartData = useMemo(() => {
    return (metrics.timeSeriesData || []).map((r) => ({
      date: r.date, conversations: r.conversations, messages: r.messages, reservations: 0,
    }));
  }, [metrics.timeSeriesData]);

  const downloadCSV = () => {
    const rows = [["date","conversations","messages","reservations (coming soon)"],
      ...chartData.map((r) => [r.date, r.conversations, r.messages, ""])];
    const csv  = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `metrics_${rangeStart}_to_${rangeEnd}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const downloadPDF = async () => {
    const [{ default: jsPDF }, { default: html2canvas }, { default: autoTable }] = await Promise.all([
      import("jspdf"),
      import("html2canvas"),
      import("jspdf-autotable"),
    ]);
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    doc.setFontSize(16); doc.text(`Metrics Report`, 40, 40);
    doc.setFontSize(11); doc.text(`Range: ${rangeStart} to ${rangeEnd}`, 40, 58);
    if (chartsRef.current) {
      const canvas   = await html2canvas(chartsRef.current, { backgroundColor: "#ffffff", scale: 2 });
      const imgData  = canvas.toDataURL("image/png");
      const pageW    = doc.internal.pageSize.getWidth();
      const maxW     = pageW - 80;
      const ratio    = canvas.width / canvas.height;
      const imgW     = maxW;
      const imgH     = imgW / ratio;
      doc.addImage(imgData, "PNG", 40, 80, imgW, imgH);
      const tableY = 80 + imgH + 20;
      autoTable(doc, {
        startY: tableY,
        head: [["Metric", "Count"]],
        body: [
          ["Conversations", metrics.summary.conversations],
          ["Messages", metrics.summary.messages],
          ["Reservations (Coming Soon)", ""],
        ],
        styles: { fontSize: 10 }, headStyles: { fillColor: [50,177,255] }, theme: "grid",
        margin: { left: 40, right: 40 },
      });
      const afterY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 16 : tableY + 16;
      autoTable(doc, {
        startY: afterY,
        head: [["Date","Conversations","Messages","Reservations (Coming Soon)"]],
        body: chartData.map((r) => [r.date, r.conversations, r.messages, ""]),
        styles: { fontSize: 9 }, headStyles: { fillColor: [71,83,85] }, theme: "striped",
        margin: { left: 40, right: 40 },
      });
    }
    doc.save(`metrics_${rangeStart}_to_${rangeEnd}.pdf`);
  };

  /* ----------------------------- UI bits ----------------------------- */
  const StatCard = ({ title, value, subtitle, variant = "blue", icon = "chat", tooltip, loading = false }) => {
    const variants = {
      // Primary accent - bold brand cyan for KEY metrics (1-2 most important)
      primary:"from-[#32B1FF] to-blue-600",     // Bold brand cyan/blue - use sparingly!
      // Secondary accent - for high-value metrics like bookings/conversions  
      success:"from-teal-500 to-cyan-600",      // Teal/cyan for success (stays in blue family)
      // Muted variants - for supporting/secondary metrics (lighter blue tones)
      muted:"from-sky-400 to-blue-500",         // Sky blue - softer than primary
      subtle:"from-blue-400 to-indigo-500",     // Blue to indigo
      disabled:"from-slate-400 to-blue-400/50 dark:from-slate-500 dark:to-blue-500/40", // Faded blue-gray
      // Legacy mappings (for backward compatibility)
      cyan:"from-[#32B1FF] to-blue-600", blue:"from-[#32B1FF] to-blue-600",
      teal:"from-teal-500 to-cyan-600", mint:"from-teal-500 to-cyan-600",
      sky:"from-sky-400 to-blue-500", aqua:"from-sky-400 to-blue-500",
      emerald:"from-teal-500 to-cyan-600", rose:"from-[#32B1FF] to-blue-600",
      indigo:"from-blue-400 to-indigo-500", amber:"from-sky-400 to-blue-500",
      slate:"from-slate-400 to-blue-400/50 dark:from-slate-500 dark:to-blue-500/40",
    };
    const icons = {
      chat:<path d="M21 15a4 4 0 0 1-4 4H7l-4 4V5a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />,
      bars:(<> <path d="M4 20V10" /><path d="M10 20V4" /><path d="M16 20v-6" /><path d="M22 20H2" /> </>),
      users:(<> <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Z" /><path d="M20 22a8 8 0 0 0-16 0" /> </>),
      msg:(<> <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 8h10M7 12h6" /> </>),
    };
    return (
      <div className={`relative rounded-2xl p-6 shadow-sm border bg-gradient-to-br ${variants[variant] ?? variants.blue} text-white min-h-[140px] flex flex-col`}>
        {/* Wrap decorative icon in its own overflow-hidden container so tooltip isn't clipped */}
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div className="absolute -top-6 -right-6 opacity-10 dark:opacity-5">
            <svg width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
              {icons[icon] ?? icons.chat}
            </svg>
          </div>
        </div>
        <div className="flex items-center gap-1.5 relative z-20">
          <div className="text-[11px] uppercase tracking-wide font-semibold">{title}</div>
          {tooltip && <InfoTip text={tooltip} iconClassName="text-white/70 hover:text-white" />}
        </div>
        {loading ? (
          <div className="mt-2 flex items-center gap-2 relative z-10">
            <svg className="animate-spin h-6 w-6 text-white/80" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-lg text-white/70">Loading...</span>
          </div>
        ) : (
          <div className="mt-2 text-3xl font-extrabold drop-shadow-sm relative z-10">{value}</div>
        )}
        {subtitle && <div className="text-xs mt-1.5 text-white/80 relative z-10">{subtitle}</div>}
      </div>
    );
  };

  // Growth Badge with dynamic color based on value (positive=green, negative=red, zero=gray)
  const GrowthBadge = ({ label, value, title }) => {
    const numValue = Number(value) || 0;
    const isPositive = numValue > 0;
    const isNegative = numValue < 0;
    
    // Color classes based on value
    const colorClasses = isPositive
      ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
      : isNegative
      ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400"
      : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400";
    
    // Arrow icon
    const ArrowIcon = isPositive ? (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
      </svg>
    ) : isNegative ? (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    ) : null;

    return (
      <span 
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-semibold transition-colors ${colorClasses}`}
        title={title}
      >
        {ArrowIcon}
        {label}: {numValue >= 0 ? "+" : ""}{numValue}%
      </span>
    );
  };

  const avgPerChat =
    metrics.summary.conversations > 0
      ? Math.round(metrics.summary.messages / metrics.summary.conversations)
      : 0;

  /* -------------------------- Product gate -------------------------- */
  const productBlocked = !!view.selectedProjectId && !!product && (
    (product === "chat" && !view.showChat) ||
    (product === "voice" && !view.showVoice)
  );
  useEffect(() => {
    if (!productBlocked) return;
    setMetrics({ timeSeriesData: [], summary: { conversations: 0, messages: 0, bookings: 0 } });
    setGrowth({
      currentPeriod: { conversations: 0, messages: 0, bookings: 0 },
      previousPeriod:{ conversations: 0, messages: 0, bookings: 0 },
      growth:        { conversations: 0, messages: 0, bookings: 0 },
    });
    setMetricsError("");
    setGrowthError("");
  }, [productBlocked]);

  // When the current product isn't available for the selected project, swap to the
  // other product if it is, otherwise redirect to the next enabled feature.
  useEffect(() => {
    if (!productBlocked) return;
    if (product === "chat" && view.showVoice) { setProduct("voice"); return; }
    if (product === "voice" && view.showChat) { setProduct("chat"); return; }
    if (view.showBookings) { navigate("/bookings"); return; }
    if (view.showCdp) { navigate("/visitors"); return; }
    if (view.showGoogleAds) { navigate("/google-ads"); return; }
    if (view.showGoogleAnalytics) { navigate("/google-analytics"); return; }
    if (view.showEventIntelligence) { navigate("/event-intelligence"); return; }
    if (view.showAim) { navigate("/ai-visibility"); return; }
    if (view.showRouteIntelligence) { navigate("/route-intelligence"); return; }
    if (view.showCompetitiveIntelligence) { navigate("/competitive-intelligence"); return; }
  }, [productBlocked, product, view.showChat, view.showVoice, view.showBookings, view.showCdp, view.showGoogleAds, view.showGoogleAnalytics, view.showEventIntelligence, view.showAim, view.showRouteIntelligence, view.showCompetitiveIntelligence, navigate]);

  // 2026-07-27 — /client-dashboard now renders the same tile grid as
  // /dashboard. The legacy chat/voice `productBlocked` empty state + the giant
  // metrics/growth body below are replaced by DashboardTileGrid, which per-tile
  // handles enablement and per-tile skeletons. All the state above is kept for
  // now (metrics/growth/product fetches) so nothing crashes on stale references;
  // the fetches simply produce data that isn't rendered anywhere. Follow-up:
  // strip the dead state + effects once we're confident nothing else consumes
  // them.
  return (
    <div className="bg-[#f7f8fa] dark:bg-gray-950 min-h-screen transition-colors duration-200">
      <ClientHeader username={username} initials={username.slice(0, 2).toUpperCase()} activePage="Dashboard" />
      <div className="flex">
        <NavSidebar activePage="home" />
        <main className="flex-1 min-w-0">
          <div className="px-6 sm:px-8 py-8 max-w-7xl mx-auto">
            <DashboardTileGrid />
          </div>
        </main>
      </div>
      <SubscribeUpsellModal
        open={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        product={subscribeProduct}
        clientName={view.clientDisplayName || ""}
      />
    </div>
  );

  // Legacy return preserved below for reference during rollout — unreachable
  // after the early return above. Delete in the follow-up cleanup.
  // eslint-disable-next-line no-unreachable
  return (
    <div className="bg-[#f7f8fa] dark:bg-gray-950 min-h-screen transition-colors duration-200">
      <ClientHeader username={username} initials={username.slice(0, 2).toUpperCase()} activePage="Dashboard" />

      <div className="flex">
        <NavSidebar activePage="home" />

        {/* Main */}
        <main className="flex-1">
          <div className="px-6 md:px-10 space-y-6 py-6">

            {/* Project selector + date picker now in ClientHeader */}

            {/* View transcripts CTA for the currently-selected project — disabled when current product isn't enabled */}
            {selectedProjectId && (() => {
              const transcriptsDisabled =
                (product === "chat" && !view.showChat) ||
                (product === "voice" && !view.showVoice) ||
                !product;
              const disabledReason = product === "voice"
                ? "Voice is not enabled for this property"
                : "Chat is not enabled for this property";
              return (
                <div className="flex justify-end">
                  <button
                    type="button"
                    disabled={transcriptsDisabled}
                    onClick={() => {
                      if (transcriptsDisabled) return;
                      const proj = projects.find((p) => p.id === selectedProjectId);
                      navigate(`/client-projects/${selectedProjectId}`, {
                        state: {
                          projectType: product,
                          projectName: proj?.name,
                          projectLogo: proj?.logo,
                        },
                      });
                    }}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition ${
                      transcriptsDisabled
                        ? "bg-gray-200 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        : "bg-[#32B1FF] text-white hover:bg-[#259ae6]"
                    }`}
                    title={transcriptsDisabled ? disabledReason : `Open ${product === "voice" ? "call" : "chat"} transcripts for the selected project`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z" />
                    </svg>
                    View transcripts
                  </button>
                </div>
              );
            })()}

            {/* 4) ANALYTICS SUMMARY CARDS */}
            {product === "voice" ? (
              /* Voice Analytics Cards */
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <StatCard 
                  title="TOTAL CALLS" 
                  value={metrics.summary.conversations.toLocaleString()} 
                  subtitle="Selected period" 
                  variant="primary" 
                  loading={metricsLoading}
                  tooltip="This shows the total number of voice calls received during the selected time period. Each incoming call is counted as one call."
                />
                <StatCard 
                  title="CALL TRANSFERS" 
                  value={(metrics.summary.transfers ?? 0).toLocaleString()} 
                  subtitle="To live agents" 
                  variant="muted" 
                  icon="bars"
                  loading={metricsLoading}
                  tooltip="This shows how many calls were transferred to a live agent or another department. Detected by analyzing conversation content for transfer-related messages."
                />
                <StatCard 
                  title="BOOKING CONVERSIONS" 
                  value={(metrics.summary.bookings ?? 0).toLocaleString()} 
                  subtitle="Confirmed bookings" 
                  variant="success" 
                  icon="msg"
                  loading={metricsLoading}
                  tooltip="This shows the number of successful bookings or appointments made through voice calls. Detected by analyzing conversation content for booking confirmations."
                />
                <StatCard 
                  title="TOTAL RESERVATIONS" 
                  value="Coming Soon" 
                  subtitle="Feature in development" 
                  variant="disabled" 
                  icon="users"
                  loading={false}
                  tooltip="Reservation tracking is coming soon. This will show the total number of reservations made through your voice assistant."
                />
              </div>
            ) : (
              /* Chat Analytics Cards */
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <StatCard 
                  title="TOTAL CONVERSATIONS" 
                  value={metrics.summary.conversations.toLocaleString()} 
                  subtitle="Selected period" 
                  variant="primary" 
                  loading={metricsLoading}
                  tooltip="This shows how many separate chats or conversations your customers started during the selected time period. Each time someone begins a new chat, it counts as one conversation."
                />
                <StatCard 
                  title="TOTAL MESSAGES" 
                  value={metrics.summary.messages.toLocaleString()} 
                  subtitle="All messages exchanged" 
                  variant="muted" 
                  icon="bars"
                  loading={metricsLoading}
                  tooltip="This is the total number of messages exchanged across all conversations. It includes both messages sent by your chatbot and messages sent by your customers. More messages can mean more detailed interactions."
                />
                <StatCard 
                  title="AVG MESSAGES/CHAT" 
                  value={Math.max(0, Math.round((metrics.summary.messages || 0) / Math.max(1, metrics.summary.conversations || 0))).toLocaleString()} 
                  subtitle="Messages per conversation" 
                  variant="muted" 
                  icon="msg"
                  loading={metricsLoading}
                  tooltip="This tells you the average number of messages in each conversation. A higher number means customers are having longer, more engaged conversations. For example, if this is 8, it means each chat typically has about 8 back-and-forth messages."
                />
                <StatCard 
                  title="TOTAL RESERVATIONS" 
                  value="Coming Soon" 
                  subtitle="Feature in development" 
                  variant="disabled" 
                  icon="users"
                  loading={false}
                  tooltip="Reservation tracking is coming soon. This will show the total number of reservations made through your chatbot."
                />
              </div>
            )}

            {/* Growth badges */}
            <div className="rounded-2xl border dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Growth vs previous period</div>
              {product === "voice" ? (
                /* Voice Growth Badges */
                <div className="mt-2 flex flex-wrap gap-2">
                  <GrowthBadge label="Calls" value={growth.growth.conversations ?? 0} title="Calls growth vs. previous period" />
                  <GrowthBadge label="Transfers" value={growth.growth.transfers ?? 0} title="Transfers growth vs. previous period" />
                  <GrowthBadge label="Bookings" value={growth.growth.bookings ?? 0} title="Bookings growth vs. previous period" />
                  <GrowthBadge label="Reservations" value={growth.growth.reservations ?? 0} title="Reservations growth vs. previous period" />
                </div>
              ) : (
                /* Chat Growth Badges */
                <div className="mt-2 flex flex-wrap gap-2">
                  <GrowthBadge label="Convos" value={growth.growth.conversations ?? 0} title="Conversations growth vs. previous period" />
                  <GrowthBadge label="Msgs" value={growth.growth.messages ?? 0} title="Messages growth vs. previous period" />
                  <GrowthBadge label="Reservations" value={growth.growth.reservations ?? 0} title="Reservations growth vs. previous period" />
                </div>
              )}
              {(growthLoading || growthError) && (
                <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  {growthLoading ? "Calculating growth…" : growthError}
                </div>
              )}
            </div>

            {/* Charts */}
            <div ref={chartsRef} className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="rounded-xl border dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
                <div className="text-sm font-semibold mb-3 text-gray-900 dark:text-gray-100">
                  {product === "voice" ? "Calls Over Time" : "Conversations vs Messages"}
                </div>
                {metricsLoading ? (
                  <div className="text-gray-500 dark:text-gray-400">Loading chart…</div>
                ) : metricsError ? (
                  <div className="text-red-600 dark:text-red-400">{metricsError}</div>
                ) : (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {product === "voice" ? (
                          <Line type="monotone" dataKey="conversations" name="Calls" stroke="#2563eb" dot={false} />
                        ) : (
                          <>
                            <Line type="monotone" dataKey="conversations" stroke="#2563eb" dot={false} />
                            <Line type="monotone" dataKey="messages" stroke="#16a34a" dot={false} />
                          </>
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div className="rounded-xl border dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
                <div className="text-sm font-semibold mb-3 text-gray-900 dark:text-gray-100">
                  {product === "voice" ? "Reservations Over Time" : "Reservations Over Time"}
                </div>
                {metricsLoading ? (
                  <div className="text-gray-500 dark:text-gray-400">Loading chart…</div>
                ) : metricsError ? (
                  <div className="text-red-600 dark:text-red-400">{metricsError}</div>
                ) : (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="reservations" fill="#f59e0b" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

          </div>
        </main>
      </div>

      <SubscribeUpsellModal
        open={subscribeOpen}
        product={subscribeProduct}
        clientId={clientId ?? ""}
        onClose={() => setSubscribeOpen(false)}
        onStart={handleStartSubscription}
      />
    </div>
  );
}

export default ClientDashboard;
